/**
 * Builds app-level React Router definitions from the frozen module registry.
 *
 * Output rows are consumed by {@link ../routes/Routes.jsx} together with
 * route chunk loaders derived from `appModuleDefinitions.js` (via `routeChunkLoaders.js`).
 *
 * @module modules/appModuleRoutes
 */

import { getModuleCatalogEntries } from "./appModuleCatalog";

/**
 * @typedef {"desktop"|"mobile"} RouteVariant
 */

/**
 * @typedef {Object} ModuleRouteDefinition
 * @property {string} moduleKey — registry key; passed to `RequireModuleAccess`
 * @property {string} path — React Router path (flat path or shell `mountPath` e.g. `/actions/*`)
 * @property {string} routeId — maps to a lazy component in `Routes.jsx` (e.g. `actions.root`, `obligations.mobile`)
 */

/**
 * Convert registry entries into app-level route rows for `Routes.jsx`.
 *
 * **Flat modules** — one row per `routePaths` entry, same `routeId`.
 * **Shell modules** — one row: `routing.mountPath` + `routing.routeId` (child routes live in `*ModuleRoot.jsx`).
 *
 * **Mobile** — when `variant === "mobile"`, skips modules without `routing.mobileEnabled`.
 * Optional `routing.mobileRouteId` swaps the component id; otherwise desktop `routeId` is reused
 * (same page, responsive CSS).
 *
 * @param {Object} [options]
 * @param {RouteVariant} [options.variant="desktop"]
 * @returns {ModuleRouteDefinition[]}
 */
export function getModuleRouteDefinitions({ variant = "desktop" } = {}) {
  const isMobile = variant === "mobile";

  return getModuleCatalogEntries().flatMap((moduleConfig) => {
    const { key: moduleKey, routePaths, routing } = moduleConfig;

    // Mobile route tree only includes modules explicitly opted in via the registry.
    if (isMobile && !routing.mobileEnabled) return [];

    // Optional alternate mobile page; omit mobileRouteId to reuse the desktop component.
    const routeId = isMobile && routing.mobileRouteId
      ? routing.mobileRouteId
      : routing.routeId;

    // Shell: single guarded mount; internal pages are owned by the module root.
    if (routing.mode === "shell") {
      return [{ moduleKey, path: routing.mountPath, routeId }];
    }

    // Flat: each registry path becomes its own app-level Route (same component per routeId).
    return routePaths.map((path) => ({ moduleKey, path, routeId }));
  });
}
